<script setup lang="ts">
import { ref } from "vue";
import { getPixelSizeForCanvas } from "../../constants/canvas";
import CanvasCore from "./CanvasCore.vue";
import { useResponsiveCanvasStyles } from "@/features/draw/composables/useUIControls";

const { getResponsiveStyles } = useResponsiveCanvasStyles();

// Define drawing state interface
interface DrawingState {
  tool: string;
  color: string;
  brushSize: number;
}

// Define selection state interface
interface SelectionState {
  selectedArea: Uint8Array;
  isSelecting: boolean;
}

// Define component props interface
interface Props {
  size: number; // Canvas size (width and height are the same)
  pixels: Uint32Array; // Merged layer pixels
  drawingState: DrawingState;
  selectionState: SelectionState;
  showGrid: boolean;
  selectedLayerId: number; // Add selectedLayerId prop
  viewMode: "pixel" | "raw"; // Add viewMode prop
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (
    e: "pixel-click",
    index: number,
    newPixels?: Uint32Array,
    isDragging?: boolean
  ): void;
  (e: "selection-change", selectedArea: Uint8Array): void;
  (e: "drawing-start"): void;
  (e: "drawing-end"): void;
  (e: "drag-end"): void;
}>();

// Size of each pixel in the canvas
const pixelSize = getPixelSizeForCanvas(props.size);

// 响应式样式计算属性
const canvasContainerStyles = getResponsiveStyles();

// Add ref to CanvasCore component
const canvasRef = ref<InstanceType<typeof CanvasCore> | null>(null);

// Handle pixel updates from CanvasCore
const handlePixelUpdate = (
  index: number,
  newPixels?: Uint32Array,
  isDragging?: boolean
) => {
  if (index >= 0) {
    // Single pixel update (eyedropper, fill)
    emit("pixel-click", index);
  } else if (newPixels) {
    // Batch update (drawing, erasing)

    emit("pixel-click", -1, newPixels, isDragging);
  }
};

// Expose exportToPng method
defineExpose({
  exportToPng: () => canvasRef.value?.exportToPng(),
});
</script>

<template>
  <div
    class="relative bg-base-200 flex justify-center overflow-hidden select-none w-full h-full"
    v-show="viewMode === 'pixel'"
  >
    <CanvasCore
      ref="canvasRef"
      :width="size"
      :height="size"
      :pixels="pixels"
      :show-grid="showGrid && viewMode === 'pixel'"
      :selected-tool="drawingState.tool"
      :current-color="drawingState.color"
      :brush-size="drawingState.brushSize"
      :pixel-size="pixelSize"
      :selected-layer-id="selectedLayerId"
      :view-mode="viewMode"
      @pixel-update="handlePixelUpdate"
      @drawing-end="$emit('drawing-end')"
    />
  </div>
</template>

<style scoped></style>
